import { Trans } from '@lingui/macro'
import { Checkbox, FormControl, FormControlLabel, FormLabel, Grid } from '@mui/material'
import { FormikCKEditor } from 'app/views/common-components/CKEditor/FormikCKEditor'
import { DefaultNumericFormat } from 'app/views/common/Formats'
import ConfigureMultilanguageTextField from 'app/views/internal/ConfigureMultilanguageTextField'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import DebouncedTextField from '../../common/DebouncedTextField'
import { FormConnectToObject } from '../../common/FormConnectToObject'
import { PdfPropsForm } from '../../common/PdfPropsForm'
import { validFieldTypesForTextField } from '../../editor/FormWizard'
import { getMainConnected } from '../../Form'
import { FormTextField } from './FormTextField'
import { FormTextInputReadOnly } from './FormTextInputReadOnly'

export function FormEditorTextField ({
  id,
  typeProps = {},
  injectable,
  injectableId,
  disabled,
  langVersion,
  depth,
  editMode,
  title,
  showPdfProps,
  showPdfComponent,
  showPrintComponent,
  showEditableComponent,
  showEditableProps,
  isInsideGroup,
  ...props
}) {
  const dispatch = useDispatch()
  const {
    required,
    forceRequired,
    expandable,
    rows,
    isAdvancedTextEditor,
    allowAddingTables,
    isEmail,
    isUrl,
    regexValidation,
    regexValidationMessage,
    lengthLimit,
    maxChar,
    isConnected,
    wordCount,
    showFieldLabel,
    requiresRequest,
    defaultValue = ''
  } = typeProps
  const [shouldUseCKEditor, setShouldUseCKEditor] =
    useState(isAdvancedTextEditor)

  useEffect(() => {
    setShouldUseCKEditor(isAdvancedTextEditor)
  }, [isAdvancedTextEditor])

  if (!editMode) {
    if (showPrintComponent || showPdfComponent) {
      return (
        <FormTextInputReadOnly
          typeProps={typeProps}
          title={title}
          {...props}
          editorPreview
        />
      )
    }

    return (
      <FormTextField
        typeProps={typeProps}
        title={title}
        defaultValue={defaultValue}
        {...props}
      />
    )
  }

  const { connectedField } = getMainConnected({ typeProps, ...props })
  const readOnly = typeProps.readOnly

  return (
    <Grid container direction='column' style={{ padding: 10 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(readOnly && connectedField)}
            disabled={!connectedField || disabled}
            onChange={(e) => {
              const toSet = { ...typeProps }
              toSet.readOnly = e.target.checked
              delete toSet.wordCount
              delete toSet.required
              delete toSet.rows
              delete toSet.lengthLimit
              delete toSet.maxChar
              delete toSet.showFieldLabel
              delete toSet.expandable
              delete toSet.requiresRequest
              dispatch({
                type: 'FIELD',
                injectable,
                depth: depth.split('.'),
                fieldName: 'typeProps',
                fieldValue: { ...toSet }
              })
            }}
          />
        }
        label={<Trans>Read only</Trans>}
      />

      {shouldUseCKEditor
        ? (
          <FormControl fullWidth style={{ marginTop: 5, marginBottom: 5 }}>
            <FormLabel><Trans>Default value</Trans></FormLabel>
            <FormikCKEditor
              name={id}
              disabled={readOnly || disabled}
              label={<Trans>Default value</Trans>}
              style={{ marginTop: 5, marginBottom: 5 }}
              allowAddingTables
              fullWidth
              variant='outlined'
              typeProps={typeProps}
              onChange={(e) => {
                const toSet = { ...typeProps }
                toSet.defaultValue = e.target.value
                if (!e.target.value) {
                  delete toSet.defaultValue
                }
                dispatch({
                  type: 'FIELD',
                  injectable,
                  depth: depth.split('.'),
                  fieldName: 'typeProps',
                  fieldValue: { ...toSet }
                })
              }}
            />
          </FormControl>
          )
        : (
          <DebouncedTextField
            value={defaultValue || ''}
            disabled={readOnly || disabled}
            style={{ marginTop: 5, marginBottom: 5 }}
            fullWidth
            label={<Trans>Default value</Trans>}
            variant='outlined'
            multiline
            onChange={(e) => {
              const toSet = { ...typeProps }
              toSet.defaultValue = e.target.value
              if (!e.target.value) {
                delete toSet.defaultValue
              }
              dispatch({
                type: 'FIELD',
                injectable,
                depth: depth.split('.'),
                fieldName: 'typeProps',
                fieldValue: { ...toSet }
              })
            }}
          />
          )}

      {!readOnly && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(required || forceRequired)}
                disabled={readOnly || forceRequired || disabled}
                onChange={(e) => {
                  const toSet = { ...typeProps }
                  toSet.required = e.target.checked
                  dispatch({
                    type: 'FIELD',
                    injectable,
                    depth: depth.split('.'),
                    fieldName: 'typeProps',
                    fieldValue: { ...toSet }
                  })
                }}
              />
              }
            label={<Trans>Required</Trans>}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(isEmail)}
                disabled={readOnly || isAdvancedTextEditor || disabled}
                onChange={(e) => {
                  const toSet = { ...typeProps }
                  toSet.isEmail = e.target.checked
                  if (e.target.checked) {
                    delete toSet.isUrl
                    // delete toSet.isPostalCode
                  }
                  dispatch({
                    type: 'FIELD',
                    injectable,
                    depth: depth.split('.'),
                    fieldName: 'typeProps',
                    fieldValue: { ...toSet }
                  })
                }}
              />
              }
            label={<Trans>Is email?</Trans>}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(isUrl)}
                disabled={readOnly || isAdvancedTextEditor || disabled}
                onChange={(e) => {
                  const toSet = { ...typeProps }
                  toSet.isUrl = e.target.checked
                  if (e.target.checked) {
                    delete toSet.isEmail
                    // delete toSet.isPostalCode
                  }
                  dispatch({
                    type: 'FIELD',
                    injectable,
                    depth: depth.split('.'),
                    fieldName: 'typeProps',
                    fieldValue: { ...toSet }
                  })
                }}
              />
              }
            label={<Trans>Is url?</Trans>}
          />

          <DebouncedTextField
            value={regexValidation || ''}
            disabled={readOnly || disabled}
            style={{ marginTop: 5, marginBottom: 5 }}
            fullWidth
            label={<Trans>Regex validation</Trans>}
            variant='outlined'
            onChange={(e) => {
              const toSet = { ...typeProps }
              toSet.regexValidation = e.target.value
              if (!e.target.value) {
                delete toSet.regexValidationMessage
              }
              dispatch({
                type: 'FIELD',
                injectable,
                depth: depth.split('.'),
                fieldName: 'typeProps',
                fieldValue: { ...toSet }
              })
            }}
          />

          {regexValidation && (
            <ConfigureMultilanguageTextField
              value={regexValidationMessage}
              label={<Trans>Regex validation message</Trans>}
              handleChange={(value) => {
                const toSet = { ...typeProps }
                toSet.regexValidationMessage = value
                dispatch({
                  type: 'FIELD',
                  injectable,
                  depth: depth.split('.'),
                  fieldName: 'typeProps',
                  fieldValue: { ...toSet }
                })
              }}
              useDebounce
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(isAdvancedTextEditor)}
                disabled={readOnly || disabled}
                onChange={(e) => {
                  const toSet = { ...typeProps }
                  setShouldUseCKEditor(e.target.value)
                  toSet.isAdvancedTextEditor = e.target.checked
                  delete toSet.showFieldLabel
                  delete toSet.expandable
                  delete toSet.isEmail
                  delete toSet.isUrl
                  delete toSet.allowAddingTables
                  toSet.rows = 1
                  dispatch({
                    type: 'FIELD',
                    injectable,
                    depth: depth.split('.'),
                    fieldName: 'typeProps',
                    fieldValue: { ...toSet }
                  })
                }}
              />
              }
            label={<Trans>Is an andvanced editor?</Trans>}
          />

          {isAdvancedTextEditor && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(allowAddingTables)}
                  disabled={readOnly || disabled}
                  onChange={(e) => {
                    const toSet = { ...typeProps }
                    toSet.allowAddingTables = e.target.checked
                    dispatch({
                      type: 'FIELD',
                      injectable,
                      depth: depth.split('.'),
                      fieldName: 'typeProps',
                      fieldValue: { ...toSet }
                    })
                  }}
                />
                }
              label={
                <Trans>FORM_EDITOR_TEXT_FIELD_SHOW_TABLE_QUESTION</Trans>
                }
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(showFieldLabel)}
                disabled={readOnly || isAdvancedTextEditor || disabled}
                onChange={(e) => {
                  const toSet = { ...typeProps }
                  toSet.showFieldLabel = e.target.checked
                  dispatch({
                    type: 'FIELD',
                    injectable,
                    depth: depth.split('.'),
                    fieldName: 'typeProps',
                    fieldValue: { ...toSet }
                  })
                }}
              />
              }
            label={<Trans>Show label in field?</Trans>}
          />

          <Grid container alignItems='center'>
            <Grid item xs style={{ padding: 5 }}>
              <DebouncedTextField
                value={rows || '1'}
                disabled={readOnly || isAdvancedTextEditor || disabled}
                style={{ marginTop: 5, marginBottom: 5 }}
                fullWidth
                label={<Trans>Rows</Trans>}
                InputProps={{ inputComponent: DefaultNumericFormat }}
                variant='outlined'
                onChange={(e) => {
                  const toSet = { ...typeProps }
                  toSet.rows = e.target.value
                  dispatch({
                    type: 'FIELD',
                    injectable,
                    depth: depth.split('.'),
                    fieldName: 'typeProps',
                    fieldValue: { ...toSet }
                  })
                }}
              />
            </Grid>

            <Grid item style={{ paddingLeft: 10 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={isAdvancedTextEditor || readOnly || disabled}
                    checked={Boolean(expandable)}
                    onChange={(e) => {
                      const toSet = { ...typeProps }
                      toSet.expandable = e.target.checked
                      dispatch({
                        type: 'FIELD',
                        injectable,
                        depth: depth.split('.'),
                        fieldName: 'typeProps',
                        fieldValue: { ...toSet }
                      })
                    }}
                  />
                  }
                label={<Trans>Allow expanding rows?</Trans>}
              />
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Checkbox
                disabled={readOnly || disabled}
                checked={Boolean(wordCount)}
                onChange={(e) => {
                  const toSet = { ...typeProps }
                  toSet.wordCount = e.target.checked
                  dispatch({
                    type: 'FIELD',
                    injectable,
                    depth: depth.split('.'),
                    fieldName: 'typeProps',
                    fieldValue: { ...toSet }
                  })
                }}
              />
              }
            label={<Trans>Show word count?</Trans>}
          />

          <FormControlLabel
            control={
              <Checkbox
                disabled={readOnly || disabled}
                checked={Boolean(lengthLimit)}
                onChange={(e) => {
                  const toSet = { ...typeProps }
                  toSet.lengthLimit = e.target.checked
                  delete toSet.maxChar
                  dispatch({
                    type: 'FIELD',
                    injectable,
                    depth: depth.split('.'),
                    fieldName: 'typeProps',
                    fieldValue: { ...toSet }
                  })
                }}
              />
              }
            label={<Trans>Custom characters limit?</Trans>}
          />
          {lengthLimit && (
            <div style={{ width: 400 }}>
              <DebouncedTextField
                disabled={disabled}
                style={{ marginTop: 5, marginBottom: 5 }}
                label={<Trans>Length limit</Trans>}
                value={maxChar}
                InputProps={{ inputComponent: DefaultNumericFormat }}
                variant='outlined'
                onChange={(e) => {
                  const toSet = { ...typeProps }
                  toSet.maxChar = e.target.value
                  dispatch({
                    type: 'FIELD',
                    injectable,
                    depth: depth.split('.'),
                    fieldName: 'typeProps',
                    fieldValue: { ...toSet }
                  })
                }}
              />
            </div>
          )}
        </>
      )}

      <FormConnectToObject
        injectable={injectable}
        injectableId={injectableId}
        typeProps={typeProps}
        depth={depth}
        validTypes={validFieldTypesForTextField}
        allowReadOnlyFields
        clearOnDisconnect={['isUrl', 'isEmail', 'requiresRequest']}
        clearOnReadOnlyField={[
          'wordCount',
          'required',
          'rows',
          'lengthLimit',
          'maxChar',
          'showFieldLabel',
          'expandable',
          'requiresRequest'
        ]}
      />
      {Boolean(isConnected && connectedField) && (
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(requiresRequest)}
              disabled={disabled}
              onChange={(e) => {
                const toSet = { ...typeProps }
                toSet.requiresRequest = e.target.checked
                dispatch({
                  type: 'FIELD',
                  injectable,
                  depth: depth.split('.'),
                  fieldName: 'typeProps',
                  fieldValue: toSet
                })
              }}
            />
          }
          label={<Trans>Requires request to change value</Trans>}
        />
      )}

      {showPdfProps && (
        <PdfPropsForm
          typeProps={typeProps}
          showWrapOption
          dispatch={dispatch}
          injectable={injectable}
          depth={depth}
          wrapDisable={disabled}
          isInsideGroup={isInsideGroup}
        />
      )}
    </Grid>
  )
}
